import type { ZodError, ZodType } from 'zod';

/**
 * Turning a Zod failure into per-field messages a form can render.
 *
 * WHY VALIDATE ON THE CLIENT AT ALL, given the server validates too: the server
 * can only answer about the first problem it finds, and only after a round
 * trip. A form with six inputs and one 400 at the top tells someone that
 * *something* is wrong; this tells them which box.
 *
 * WHY THE SAME SCHEMA: the two checks must not drift. Rules live once, in
 * `src/shared/schemas/`, and both sides import them — so a rule loosened on the
 * server cannot leave the form still refusing what the server now accepts,
 * which is exactly how the add-customer form came to demand an email that
 * nothing marked required.
 */
export type FieldErrors = Record<string, string>;

/**
 * The first message per field, keyed by the field's top-level name.
 *
 * First rather than all: a single input showing three complaints at once is
 * noise, and Zod stops at the first failing check per field in most cases
 * anyway. Nested paths collapse to their first segment, because that is the
 * name the input is registered under.
 */
export function fieldErrors(error: ZodError): FieldErrors {
  const out: FieldErrors = {};
  for (const issue of error.issues) {
    const key = issue.path.find((p): p is string => typeof p === 'string');
    if (key === undefined) continue;
    if (out[key] === undefined) {
      out[key] = issue.message;
    }
  }
  return out;
}

/**
 * A message that belongs to the form rather than to any one input.
 *
 * A cross-field rule ("enter at least one detail") has no single wrong box to
 * point at. Zod's `.refine()` on an object either carries no path or is given
 * an arbitrary one, so without this the message would either vanish or attach
 * itself to a field the person has no reason to look at.
 */
export function formError(error: ZodError): string | null {
  const issue = error.issues.find((i) => i.path.length === 0);
  return issue ? issue.message : null;
}

/**
 * Validate a value, returning either the parsed data or the errors to render.
 *
 * The parsed data is deliberately part of the result: it is the TRANSFORMED
 * value — trimmed strings, `''` normalized to undefined — and sending the raw
 * form state instead would put back exactly the empty strings the schema exists
 * to strip.
 */
export type ValidationResult<T> =
  { ok: true; data: T } | { ok: false; fields: FieldErrors; form: string | null };

export function validate<T>(schema: ZodType<T>, value: unknown): ValidationResult<T> {
  const parsed = schema.safeParse(value);
  if (parsed.success) {
    return { ok: true, data: parsed.data };
  }
  const fields = fieldErrors(parsed.error);
  const form = formError(parsed.error);
  return {
    ok: false,
    fields,
    // A refine given a `path` lands in `fields` and never in `form`, which
    // would leave a cross-field failure invisible if the form only rendered
    // `form`. Fall back to the first message of any kind so something is always
    // shown rather than the submit button silently doing nothing.
    form:
      form ?? (Object.keys(fields).length === 0 ? (parsed.error.issues[0]?.message ?? null) : null),
  };
}
